/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
    getSortedRowModel,
    getFilteredRowModel,
} from '@tanstack/react-table';
import { TBody, TD, TH, THead, TR, Table, Pagination, TableWrapper, StickyTypeCell, Flexbox, SorterButton, Loading, TextInput, Tooltip, Button } from '@sede-enterprise/shell-ds-react-framework';
import { useVirtualized } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/hooks/useVirtualized';
import "./DataTable.css"
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const tableGap = "12px";



const DataTable = ({ data, columns, count, currentPage, handleCurrentPageChange, handleOrderChange, INIT_ORDER, INIT_ORDER_DIRECTION, loading, highlightSensor, highlightCML, paginationMode, isModel = false, isCancui = false, handleRowClick = null }) => {
    

    const history = useHistory()

    const [columnFilters, setColumnFilters] = useState([]);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const [sorting, setSorting] = useState([
        {
            id: INIT_ORDER,
            desc: INIT_ORDER_DIRECTION === 1,
        }
    ]);

    // Helper to get the sorting function for a column
    const getSortFn = (colId) => {
        const col = columns.find(c => c.id === colId);
        // Use accessorFn if available, else fallback to direct property
        return col?.accessorFn
            ? (row) => col.accessorFn(row)
            : (row) => row[colId];
    };

    // Sort data client-side if needed
    const sortedData = useMemo(() => {
        if (paginationMode === 'client' && sorting.length > 0) {
            const { id, desc } = sorting[0];
            // check if sorting function available in column definition, if not use default sorting
            const col = columns.find(c => c.id === id);
            if (col?.sortingFn){
                return col?.sortingFn([...data], id, desc);
            }
            const sortFn = getSortFn(id);
            return [...data].sort((a, b) => {
                const aValue = sortFn(a);
                const bValue = sortFn(b);
                if (aValue == null && bValue == null) return 0;
                if (aValue == null) return 1;
                if (bValue == null) return -1;
                if (aValue > bValue) return desc ? -1 : 1;
                if (aValue < bValue) return desc ? 1 : -1;
                return 0;
            });
        }
        return data;
    }, [data, sorting, columns, paginationMode]);


    // Calculate filtered data count for pagination
    const filteredData = useMemo(() => {
        if (paginationMode === 'client' && isCancui && columnFilters.length > 0) {
            return sortedData.filter(row => {
                return columnFilters.every(filter => {
                    const column = columns.find(col => col.id === filter.id);
                    const value = column?.accessorFn ? column.accessorFn(row) : row[filter.id];
                    const filterValue = filter.value.toLowerCase();
                    return value && String(value).toLowerCase().includes(filterValue);
                });
            });
        }
        return sortedData;
    }, [sortedData, columnFilters, isCancui, columns, paginationMode]);

    // Get total count for pagination
    const totalCount = useMemo(() => {
        if (paginationMode === 'client') {
            return filteredData.length;
        }
        return count;
    }, [paginationMode, filteredData, count]);

    // Update paginatedData to use filteredData
    const paginatedData = useMemo(() => {
        if (paginationMode === 'client') {
            const startIndex = pageSize * (currentPage - 1);
            return filteredData.slice(startIndex, startIndex + pageSize);
        }
        return sortedData;
    }, [filteredData, pageSize, currentPage, paginationMode]);



    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize],
    );

    const handleOnChange = useCallback((current, changedPageSize) => {
        table.setPageSize(changedPageSize);
        table.setPageIndex(current - 1);
        handleCurrentPageChange(current);
    }, []);

    const table = useReactTable({
        data: paginatedData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            sorting,
            pagination,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getSortedRowModel: getSortedRowModel(),
        rowCount: data?.length,
        enableSortingRemoval: false,
        manualSorting: !isModel,
        enableColumnResizing: true,
        onPaginationChange: setPagination,
        columnResizeMode: 'onChange',
    });

    useEffect(() => {
        if (sorting.length > 0) {
            const sort = sorting[0];
            handleOrderChange(sort.id, sort.desc);
        }

    }, [sorting])

    const { tableContainerRef, virtualRows, rows, paddingTop, paddingBottom, EmptyRow } = useVirtualized(table);


    let highlightRowIndex = -1;

    if (highlightSensor) {
        highlightRowIndex = rows.findIndex(row => row.original.id === highlightSensor);
    }

    if (highlightCML) {
        highlightRowIndex = rows.findIndex(row => row.original.cmLocation.shortName === highlightCML);
    }

    // Add reset to page 1 when filters change
    useEffect(() => {
        if (columnFilters.length > 0) {
            setPagination(prev => ({
                ...prev,
                pageIndex: 0
            }));
            handleCurrentPageChange(1);
        }
    }, [columnFilters]);

    return (
        <>
            <TableWrapper style={{ width: "100%" }} virtualized ref={tableContainerRef} id='table-id-1'>
                <Table style={{ width: "100%" }} id='table-id-2'>
                    <THead id='table-id-3'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TR key={headerGroup.id} depth={headerGroup.depth} sticky stickyType={StickyTypeCell.Header} className='table-header' id='table-id-4'>
                                {headerGroup.headers.map((header) => (
                                    <TH
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        id='table-id-5'
                                    >
                                        {header.isPlaceholder ? null : (
                                            <Flexbox gap={tableGap} alignItems="center" id='table-id-6'>
                                                {flexRender(header.column.columnDef.header, header.getContext())}

                                                {header.column.getCanSort() && <SorterButton
                                                    id='table-id-7'
                                                    sortDirection={header.column.getIsSorted() || undefined}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                />}

                                            </Flexbox>
                                        )}


                                    </TH>
                                ))}
                            </TR>
                        ))}
                    </THead>
                    <TBody
                        id='table-id-8'>
                        {loading && (
                            <TR id='table-id-9'>
                                <TD colSpan={columns.length} id='table-id-10'>
                                    <div className='loading-container' id='table-id-11' role='status'>
                                        <Loading id='table-id-12' />
                                    </div>
                                </TD>
                            </TR>
                        )}
                        <EmptyRow padding={paddingTop} id='table-id-13' />
                        {!loading && virtualRows.map((virtualRow) => {
                            const row = rows[virtualRow.index];
                            return (
                                <TR key={row.id} id={(isModel || isCancui || handleRowClick) ? 'modelListrow' : 'tablerow'} sentiment={+row.id === highlightRowIndex ? 'positive' : 'default'} className={+row.id === highlightRowIndex ? 'positive' : 'default'}
                                    onClick={() => isModel ? history.push('/models/' + row.original.id) : isCancui ? history.push('/cancui/' + row.original.equipment.id) : handleRowClick ? handleRowClick(row.original) : null}
                                >
                                    {row.getVisibleCells().map((cell, idx) => {
                                        //check if cell value is an array then mark it as custom cell to apply different styling
                                        const cancuiCustomCell = isCancui && Array.isArray(cell.getValue());
                                        const customFirstColumnBeforeSplit = isCancui && cell.column.id === 'zones';
                                        return (
                                            <TD key={cell.id} style={{padding: cancuiCustomCell ? 0 : '', borderLeft: customFirstColumnBeforeSplit ? '1px solid #eee' : ''}} id='table-id-15'>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TD>
                                        )
                                    })}
                                </TR>
                            );
                        })}
                        <EmptyRow padding={paddingBottom} id='table-id-16' />
                    </TBody>
                </Table>
            </TableWrapper>
            <div className='pagination-wrapper' id='table-id-17'>

                <Pagination
                    total={totalCount}
                    pageSize={table.getState().pagination.pageSize}
                    onChange={handleOnChange}
                    current={currentPage}
                    id='table-id-18'
                />
            </div>
        </>
    );
}

export default DataTable